import { useEffect, useState } from 'react';

import { isPast, isValid } from 'date-fns';
import { useIntl } from 'react-intl';

import { IDeliveryDropoff } from '@rbi-ctg/menu';
import { useLocale } from 'state/intl';
import { LANGUAGES } from 'state/intl/types';
import { DeliveryStatus } from 'state/order/types';
import { formatDistanceToNow } from 'utils/language/format-date';

import useInterval from '../use-interval';

const ONE_MINUTE_MS = 60000;

const calcEtaRange = (
  text: Record<'delivered' | 'calculatingEta', string>,
  language: LANGUAGES,
  status: DeliveryStatus,
  dropoff?: IDeliveryDropoff
) => {
  if (status === DeliveryStatus.ORDER_DROPPED_OFF) {
    return text.delivered;
  }

  if (!dropoff) {
    return text.calculatingEta;
  }

  const { arrivalTime, beforeTime } = dropoff;

  // Bringg uses arrivalTime as ETA and beforeTime as a pessimistic ETA
  // We will use the latest of the 2 ETAs since the beforeTime is used on order start
  // but the arrivalTime is updated later on if a driver is unassigned, etc
  // Once the drive is on their way Bringg updates arrivalTime to be accurate based on driving time
  // so we will use that
  const arrivalDateTime = new Date(arrivalTime);
  const beforeDateTime = new Date(beforeTime);

  const pessimisticEta = arrivalDateTime > beforeDateTime ? arrivalDateTime : beforeDateTime;
  const deliveryDateTime =
    status === DeliveryStatus.ORDER_PICKED_UP ? arrivalDateTime : pessimisticEta;

  if (!isValid(deliveryDateTime)) {
    return text.calculatingEta;
  }

  if (isPast(deliveryDateTime)) {
    return text.calculatingEta;
  }

  return formatDistanceToNow(deliveryDateTime, language);
};

export const useEtaCounter = (status: DeliveryStatus, dropoff?: IDeliveryDropoff) => {
  const { language } = useLocale();
  const { formatMessage } = useIntl();
  const text = {
    delivered: formatMessage({ id: 'delivered' }),
    calculatingEta: formatMessage({ id: 'calculatingEta' }),
  };

  const [etaString, setEtaString] = useState<string>(calcEtaRange(text, language, status, dropoff));

  // Update string when props change: poll of new data
  useEffect(() => {
    setEtaString(calcEtaRange(text, language, status, dropoff));
  }, [status, dropoff, text, language]);

  // Update string in 1 minute intervals to refresh counter
  useInterval(() => {
    const newEtaString = calcEtaRange(text, language, status, dropoff);
    setEtaString(newEtaString);
  }, ONE_MINUTE_MS);

  return etaString;
};
